<script setup lang="ts">
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CollapsibleSection from './CollapsibleSection.vue';
import type { AddonGroup } from './ProductAddonGroupCard.vue';

const { t } = useI18n({ useScope: 'global' });

// Lecture seule + attach/detach uniquement : le CRUD complet (créer,
// modifier, supprimer) d'un groupe d'options vit dans CommerceDetailPage.vue
// (onglet Catalogue > Groupes d'options), pas ici.
defineProps<{
  addonGroups: Ref<AddonGroup[]>;
  onOpenAttachPicker: () => void;
  onDetachGroup: (group: AddonGroup) => void;
  isDetachingGroupId: string | null;
  canAttachExisting: boolean;
}>();

function optionsSummary(g: AddonGroup): string {
  const count = g.options.filter(
    (o) => o.contentFr.trim() || o.contentEn.trim() || o.contentNl.trim(),
  ).length;
  return count === 1
    ? t('product.optionCount', { count })
    : t('product.optionCountPlural', { count });
}
</script>

<template>
  <CollapsibleSection
    section-key="addonGroups"
    :title="t('product.addonGroupsOptional')"
    :subtitle="t('product.addonGroupsSubtitleEdit')"
    icon="pi-th-large"
    :default-open="true"
  >
    <div v-if="canAttachExisting" class="epp__group-add-row">
      <button class="epp__add-btn" @click="onOpenAttachPicker">
        <i class="pi pi-link" /> {{ t('product.attachGroupAction') }}
      </button>
    </div>

    <div v-if="addonGroups.value.length" class="epp__groups">
      <div
        v-for="(g, gi) in addonGroups.value"
        :key="gi"
        class="epp__group-summary-card"
      >
        <div class="epp__group-summary-info">
          <span class="epp__group-summary-title">
            {{ g.titleFr || g.titleEn || g.titleNl || t('product.groupTitle') }}
          </span>
          <span class="epp__group-summary-meta">
            {{ optionsSummary(g) }} · {{ t('product.qtyMinMax') }}
            {{ g.minQty }} / {{ g.maxQty }}
          </span>
        </div>
        <button
          class="epp__add-btn epp__add-btn--sm"
          :disabled="isDetachingGroupId === g.id"
          :title="t('product.detachGroupTooltip')"
          @click="onDetachGroup(g)"
        >
          <i
            :class="
              isDetachingGroupId === g.id
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-unlink'
            "
          />
          {{ t('product.detachGroupAction') }}
        </button>
      </div>
    </div>
    <div v-else class="epp__empty">
      <i class="pi pi-th-large epp__empty-icon" />
      <p>{{ t('product.noGroupsHint') }}</p>
    </div>
  </CollapsibleSection>
</template>

<style scoped>
.epp__group-add-row {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
  margin-bottom: 1rem;
}
.epp__group-summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.875rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
}
.epp__group-summary-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}
.epp__group-summary-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.epp__group-summary-meta {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
[data-theme='dark'] .epp__group-summary-card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
</style>

<style>
/* Unscoped: .epp__groups est utilisé par d'autres onglets du même bundle. */
.epp__groups {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
</style>
